import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { pluralize, pluralizeWithCount } from "@common/helpers/string";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import { Button } from "@design-system/Button/Button";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { useCommands } from "@components/commands/useCommands";
import { CommandsDirectory } from "@components/commands/lib";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import {
    useCounterpartyBulkUpdate1099Mutation,
    useCounterpartyBulkMarkEligible1099ReviewedMutation,
    useCounterpartyBulkDeleteMutation,
} from "@api/counterparty.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { isViewOnlyRefusalError } from "@lib/viewOnlyRefusal";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { HStack } from "@design-system/HStack/HStack";
import { TaskType } from "@common/types/task";
import { useOverlays } from "contexts/OverlayContext";
import { buildBulkCounterpartiesTaskView } from "@components/tasks/TaskBody/Counterparties/counterpartiesTaskView";
import { DirectoryContext } from "./Directory.context";
import { CounterpartyDirectoryViewRow } from "./counterpartyDirectoryViewTypes";
import { DirectoryReviewSelectedButton } from "./DirectoryReviewSelectedButton";
import { DirectoryAskAboutSelectedButton } from "./DirectoryAskAboutSelectedButton";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

interface Props {
    totalCounterparties: number;
    rows?: CounterpartyDirectoryViewRow[];
    reviewMode?: "review" | "unreview";
}

export const DirectoryCommandBar: React.FC<Props> = ({
    totalCounterparties,
    rows = [],
    reviewMode,
}) => {
    const { rowSelection, clearRowSelection, view, filters } =
        useContext(DirectoryContext);
    const { openOverlay } = useOverlays();
    const { open, registerOpenOverride, unregisterOpenOverride } =
        useCommands();
    const { toast, error: toastError } = useToaster();
    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const bulkUpdate1099 = useCounterpartyBulkUpdate1099Mutation();
    const bulkMarkEligible1099Reviewed =
        useCounterpartyBulkMarkEligible1099ReviewedMutation();
    const bulkDelete = useCounterpartyBulkDeleteMutation();

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    const selectedIds = useMemo(
        () =>
            Object.entries(rowSelection)
                .filter(([, selected]) => selected)
                .map(([id]) => id),
        [rowSelection],
    );
    const selectedCount = selectedIds.length;
    const hasSelection = selectedCount > 0;
    const selectedRows = useMemo(
        () => rows.filter((row) => selectedIds.includes(row.id)),
        [rows, selectedIds],
    );

    // Runs the eligibility update and, only inside a review task
    // (Form1099TaskBody sets `reviewMode`), the ELIGIBLE_1099 review-marking as
    // two explicit operations with independent failure handling — mirroring
    // CounterpartyEligible1099Cell's single-row toggle, which surfaces a
    // review-marking failure separately from the eligibility change instead of
    // silently reporting success.
    const applyBulk1099 = useCallback(
        async (isEligible1099: boolean | null, successMessage: string) => {
            try {
                await bulkUpdate1099.mutateAsync({
                    counterpartyIds: selectedIds,
                    isEligible1099,
                });
            } catch (err) {
                if (!isViewOnlyRefusalError(err)) {
                    toastError("Failed to update counterparties");
                }
                return;
            }

            if (reviewMode) {
                try {
                    await bulkMarkEligible1099Reviewed.mutateAsync(selectedIds);
                } catch {
                    toastError(
                        "Failed to mark 1099 eligibility as reviewed. Please try again.",
                    );
                    return;
                }
            }

            toast(successMessage);
            clearRowSelection();
        },
        [
            bulkUpdate1099,
            bulkMarkEligible1099Reviewed,
            selectedIds,
            reviewMode,
            toast,
            toastError,
            clearRowSelection,
        ],
    );

    const handleMark1099 = useCallback(
        () =>
            applyBulk1099(
                true,
                `Marked ${pluralizeWithCount("counterparty", selectedCount)} as 1099 eligible`,
            ),
        [applyBulk1099, selectedCount],
    );

    const handleUnmark1099 = useCallback(
        () =>
            applyBulk1099(
                null,
                `Unmarked ${pluralizeWithCount("counterparty", selectedCount)} as 1099 eligible`,
            ),
        [applyBulk1099, selectedCount],
    );

    const handleRequestDelete = useCallback(() => {
        setIsDeleteConfirmOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        try {
            await bulkDelete.mutateAsync(selectedIds);
            toast(
                `Deleted ${pluralizeWithCount("counterparty", selectedCount)}`,
            );
            clearRowSelection();
        } catch (err) {
            if (!isViewOnlyRefusalError(err)) {
                toastError("Failed to delete counterparties");
            }
        } finally {
            setIsDeleteConfirmOpen(false);
        }
    }, [
        bulkDelete,
        selectedIds,
        selectedCount,
        toast,
        toastError,
        clearRowSelection,
    ]);

    const handleCreateTask = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        openOverlay("createTask", {
            type: TaskType.COUNTERPARTIES,
            viewConfig: buildBulkCounterpartiesTaskView({
                view,
                scope: filters,
                counterpartiesIds: selectedIds,
            }),
            initialValues: { entityIds: filters.entityIds },
        });
    }, [refuseIfViewOnly, openOverlay, view, filters, selectedIds]);

    const handleOpenActions = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        open(CommandsDirectory.COUNTERPARTY_BULK, {
            selectedCounterpartyCount: selectedCount,
            onCounterpartyBulkMark1099: handleMark1099,
            onCounterpartyBulkUnmark1099: handleUnmark1099,
            onCounterpartyBulkDelete: handleRequestDelete,
            onCounterpartyBulkCreateTask: handleCreateTask,
        });
    }, [
        refuseIfViewOnly,
        open,
        selectedCount,
        handleMark1099,
        handleUnmark1099,
        handleRequestDelete,
        handleCreateTask,
    ]);

    // Without this, mod+k while rows are selected falls through to the
    // global command palette default (AllCommandsList) instead of the
    // counterparty bulk actions relevant to the current selection.
    const handleOpenActionsRef = useRef(handleOpenActions);
    handleOpenActionsRef.current = handleOpenActions;

    useEffect(() => {
        if (hasSelection) {
            registerOpenOverride(() => handleOpenActionsRef.current());
            return () => unregisterOpenOverride();
        }
        unregisterOpenOverride();
    }, [hasSelection, registerOpenOverride, unregisterOpenOverride]);

    return (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <HStack gap={8}>
                <CommandIslandTotal>
                    {pluralizeWithCount("counterparty", totalCounterparties)}
                </CommandIslandTotal>

                {hasSelection && (
                    <Tooltip
                        key={selectedCount}
                        content={`Deselect ${selectedCount} ${pluralize("counterparty", selectedCount)}`}
                    >
                        <Button
                            variant="primary"
                            size="md"
                            onClick={clearRowSelection}
                        >
                            {selectedCount} selected
                        </Button>
                    </Tooltip>
                )}
            </HStack>

            {hasSelection && (
                <HStack gap={8}>
                    <Tooltip content="Open bulk actions">
                        <Button
                            variant="primary"
                            size="md"
                            rightIcon={
                                <span data-slot-box="">
                                    <Shortcut keys="mod+k" />
                                </span>
                            }
                            onClick={handleOpenActions}
                        >
                            Actions
                        </Button>
                    </Tooltip>
                    <DirectoryAskAboutSelectedButton
                        selectedRows={selectedRows}
                        onClearSelection={clearRowSelection}
                    />
                    {reviewMode && (
                        <DirectoryReviewSelectedButton
                            selectedRows={selectedRows}
                            mode={reviewMode}
                            onClearSelection={clearRowSelection}
                        />
                    )}
                </HStack>
            )}

            <ConfirmationModal
                show={isDeleteConfirmOpen}
                title={`Delete ${pluralizeWithCount("counterparty", selectedCount)}?`}
                question="This permanently deletes the selected counterparties and unassigns them from any related journal entries. This action cannot be undone."
                yes="Delete"
                yesBtnVariant="destructive"
                no="Cancel"
                onConfirm={handleConfirmDelete}
                onReject={() => setIsDeleteConfirmOpen(false)}
                onHide={() => setIsDeleteConfirmOpen(false)}
            />
        </CommandIsland>
    );
};
